// Tab Métodos — a razão de ser do painel: habilitar/desabilitar método de
// pagamento da loja, com comissão e parcelas visíveis.
//
// O CRUD é GRANULAR (POST/PUT/DELETE por método) de propósito: o PUT legado de
// store substitui a coleção inteira e apagaria o que não viesse no payload.
//
// Toggle é OTIMISTA com rollback (ver a fábrica no service): o switch responde
// na hora e volta sozinho se o backend recusar.
import { PlusIcon, Trash2Icon } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { cn } from "@/lib/utils"
import {
  useAddMerchantPaymentMethodMutation,
  useAdminMerchantPaymentMethods,
  useRemoveMerchantPaymentMethodMutation,
  useUpdateMerchantPaymentMethodMutation,
} from "@/services/admin/merchants"
import { usePaymentMethodCatalog } from "@/services/admin/payment-method-catalog"
import type {
  AdminMerchantPaymentMethod,
  MerchantPaymentMethodName,
} from "@/types/admin-merchant"

import {
  availableMethodsToAdd,
  formatCurrency,
  formatPercentage,
} from "../utils"

const panelPrefix = "pages.adminMerchants"

type MerchantMethodsTabProps = {
  uuid: string
}

// Confirmação exigida para as ações que TIRAM capacidade de venda da loja.
type PendingAction =
  | {
      type: "deactivate"
      method: MerchantPaymentMethodName
    }
  | { type: "remove"; method: MerchantPaymentMethodName }

export const MerchantMethodsTab = ({
  uuid,
}: MerchantMethodsTabProps) => {
  const { t } = useTranslation()
  const { data, isLoading, isError, refetch } =
    useAdminMerchantPaymentMethods(uuid)
  const { data: catalog } = usePaymentMethodCatalog()

  const addMutation =
    useAddMerchantPaymentMethodMutation(uuid)
  const updateMutation =
    useUpdateMerchantPaymentMethodMutation(uuid)
  const removeMutation =
    useRemoveMerchantPaymentMethodMutation(uuid)

  const [pendingAction, setPendingAction] =
    useState<PendingAction | null>(null)

  const isMutating =
    addMutation.isPending ||
    updateMutation.isPending ||
    removeMutation.isPending

  if (isLoading) {
    return (
      <div
        data-testid="merchant-methods-skeleton"
        className="space-y-3"
      >
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton
            key={`method-skeleton-${index}`}
            className="h-20 w-full rounded-xl"
          />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-6">
        <p className="text-center text-sm text-red-700">
          {t(`${panelPrefix}.methods.loadError`)}
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => refetch()}
        >
          {t(`${panelPrefix}.retry`)}
        </Button>
      </div>
    )
  }

  // Mesma armadilha do 204: corpo vazio chega como string, não undefined.
  const methods: AdminMerchantPaymentMethod[] =
    Array.isArray(data) ? data : []
  const available = availableMethodsToAdd(
    methods,
    catalog ?? []
  )

  // Rótulo traduzido; sem tradução, cai na descrição vinda do catálogo (o
  // backoffice já manda algo legível) e só então no nome do enum.
  const methodLabel = (method: string) =>
    t(`${panelPrefix}.methods.names.${method}`, {
      defaultValue:
        catalog?.find(
          (item) => item.paymentMethod === method
        )?.description ?? method,
    })

  const handleToggle = (
    method: AdminMerchantPaymentMethod,
    nextActive: boolean
  ) => {
    // Desativar tira capacidade de venda: confirma antes.
    if (!nextActive) {
      setPendingAction({
        type: "deactivate",
        method: method.paymentMethod,
      })
      return
    }
    updateMutation.mutate({
      method: method.paymentMethod,
      payload: { active: true },
    })
  }

  const handleConfirmPending = () => {
    if (!pendingAction) return
    if (pendingAction.type === "deactivate") {
      updateMutation.mutate({
        method: pendingAction.method,
        payload: { active: false },
      })
    } else {
      removeMutation.mutate(pendingAction.method)
    }
    setPendingAction(null)
  }

  return (
    <div className="space-y-4">
      {methods.length === 0 && (
        <div
          data-testid="merchant-methods-empty"
          className="flex flex-col items-center gap-2 rounded-xl border border-dashed px-6 py-14 text-center"
        >
          <p className="text-sm text-muted-foreground">
            {t(`${panelPrefix}.methods.empty`)}
          </p>
        </div>
      )}

      {methods.length > 0 && (
        <ul className="space-y-3">
          {methods.map((method) => (
            <li
              key={method.paymentMethod}
              data-testid={`merchant-method-${method.paymentMethod}`}
              className={cn(
                "flex flex-wrap items-center justify-between gap-4 rounded-xl border border-l-4 bg-card p-4 shadow-sm transition-shadow hover:shadow-md",
                method.active
                  ? "border-l-emerald-500"
                  : "border-l-zinc-300"
              )}
            >
              <div className="flex min-w-0 flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold">
                    {methodLabel(method.paymentMethod)}
                  </span>
                  <Badge
                    variant="outline"
                    className={cn(
                      "rounded-full border font-medium",
                      method.active
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-zinc-200 bg-zinc-50 text-zinc-500"
                    )}
                  >
                    {t(
                      `${panelPrefix}.${method.active ? "badgeActive" : "badgeInactive"}`
                    )}
                  </Badge>
                </div>
                {/* Valores financeiros com tabular-nums e Intl (rule 01). */}
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs tabular-nums text-muted-foreground">
                  <span>
                    {t(
                      `${panelPrefix}.methods.fixedCommission`
                    )}
                    :{" "}
                    {formatCurrency(method.fixedCommission)}
                  </span>
                  <span>
                    {t(
                      `${panelPrefix}.methods.percentageCommission`
                    )}
                    :{" "}
                    {formatPercentage(
                      method.percentageCommission
                    )}
                  </span>
                  <span>
                    {t(
                      `${panelPrefix}.methods.installments`
                    )}
                    : {method.installmentCount ?? "—"}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={method.active}
                  disabled={isMutating}
                  aria-label={t(
                    `${panelPrefix}.methods.toggleAria`,
                    {
                      method: methodLabel(
                        method.paymentMethod
                      ),
                    }
                  )}
                  onCheckedChange={(nextActive) =>
                    handleToggle(method, nextActive)
                  }
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={isMutating}
                  aria-label={t(
                    `${panelPrefix}.methods.removeAria`,
                    {
                      method: methodLabel(
                        method.paymentMethod
                      ),
                    }
                  )}
                  onClick={() =>
                    setPendingAction({
                      type: "remove",
                      method: method.paymentMethod,
                    })
                  }
                >
                  <Trash2Icon
                    className="size-4"
                    aria-hidden="true"
                  />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {available.length > 0 && (
        <section className="rounded-xl border border-dashed p-4">
          <h3 className="mb-3 text-sm font-semibold">
            {t(`${panelPrefix}.methods.addTitle`)}
          </h3>
          <div className="flex flex-wrap gap-2">
            {available.map((method) => (
              <Button
                key={method}
                type="button"
                variant="outline"
                size="sm"
                disabled={isMutating}
                onClick={() =>
                  addMutation.mutate({
                    paymentMethod: method,
                    active: true,
                  })
                }
              >
                <PlusIcon
                  className="mr-1 size-4"
                  aria-hidden="true"
                />
                {methodLabel(method)}
              </Button>
            ))}
          </div>
        </section>
      )}

      <AlertDialog
        open={pendingAction !== null}
        onOpenChange={(open) =>
          !open && setPendingAction(null)
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingAction?.type === "remove"
                ? t(`${panelPrefix}.methods.removeTitle`)
                : t(
                    `${panelPrefix}.methods.deactivateTitle`
                  )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAction?.type === "remove"
                ? t(
                    `${panelPrefix}.methods.removeDescription`
                  )
                : t(
                    `${panelPrefix}.methods.deactivateDescription`
                  )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(`${panelPrefix}.cancel`)}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmPending}
            >
              {t(`${panelPrefix}.confirm`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
